import { Button, Page, TextField, Select } from '../components/forms';
import { useEffect, useState } from 'react';
import {
  Layout,
  Card,
  Text,
  BlockStack,
  InlineStack,
  Tabs,
  Banner,
  DataTable,
  Autocomplete,
  Icon,
} from '@shopify/polaris';
import { PlusIcon, SearchIcon } from '@shopify/polaris-icons';
import { useAuth } from '../App';
import { useTransferCommands } from '../components/inventory/useTransferCommands';
import { useApi } from '../hooks/useApi';

interface Product {
  id: number;
  sku: string;
  title: string;
  barcode: string;
}

interface Warehouse {
  id: number;
  name: string;
  is_default: number;
}

interface Location {
  id: number;
  code: string;
  zone: string;
  warehouse_id: number;
}

interface OperationItem {
  product_id: number;
  sku: string;
  title: string;
  quantity: number;
  reason?: string;
}

interface StockLocation {
  stock_level_id: number;
  location_id: number;
  quantity: number;
  reserved: number;
  available: number;
  location_code: string;
  zone: string;
  warehouse_id: number;
  warehouse_name: string;
}

interface Movement {
  id: number;
  product_id: number;
  sku: string;
  product_title: string;
  from_location_code: string | null;
  to_location_code: string | null;
  from_warehouse_name: string | null;
  to_warehouse_name: string | null;
  quantity: number;
  type: string;
  reference: string | null;
  notes: string | null;
  user_id: string | null;
  created_at: string;
}

export default function InventoryOperations() {
  const { user, currentShop, currentOrganization } = useAuth();
  return <InventoryOperationsPage key={`${user?.id}-${currentShop?.id}-${currentOrganization?.id}`} />;
}
function InventoryOperationsPage() {
  const transfers = useTransferCommands();
  const { fetchApi, error } = useApi();
  const [selectedTab, setSelectedTab] = useState(0);
  const [warehouses, setWarehouses] = useState<Warehouse[]>([]);
  const [locations, setLocations] = useState<Location[]>([]);
  const [success, setSuccess] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  // Product search
  const [searchValue, setSearchValue] = useState('');
  const [searchResults, setSearchResults] = useState<Product[]>([]);
  const [selectedProduct, setSelectedProduct] = useState<Product | null>(null);

  // Operation items (batch mode)
  const [items, setItems] = useState<OperationItem[]>([]);

  // Common form fields
  const [quantity, setQuantity] = useState('1');
  const [reference, setReference] = useState('');
  const [notes, setNotes] = useState('');
  const [reason, setReason] = useState('');

  // Location selectors
  const [selectedWarehouse, setSelectedWarehouse] = useState('');
  const [selectedLocation, setSelectedLocation] = useState('');

  // Existing stock locations for current product
  const [existingStockLocations, setExistingStockLocations] = useState<StockLocation[]>([]);
  const [suggestedLocation, setSuggestedLocation] = useState<StockLocation | null>(null);

  // History/Audit trail
  const [movements, setMovements] = useState<Movement[]>([]);
  const [movementsLoading, setMovementsLoading] = useState(false);
  const [movementPage, setMovementPage] = useState(1);
  const [movementTotalPages, setMovementTotalPages] = useState(1);
  const [movementFilter, setMovementFilter] = useState('');

  const tabs = [
    { id: 'receive', content: 'Receive Stock', panelID: 'receive-panel' },
    { id: 'dispatch', content: 'Dispatch/Write-off', panelID: 'dispatch-panel' },
    { id: 'transfer', content: 'Transfer', panelID: 'transfer-panel' },
    { id: 'adjust', content: 'Adjustments', panelID: 'adjust-panel' },
    { id: 'history', content: 'Audit Trail', panelID: 'history-panel' },
  ];

  useEffect(() => {
    loadWarehouses();
  }, []);

  useEffect(() => {
    if (selectedTab === 4) {
      loadMovements();
    }
  }, [selectedTab, movementPage, movementFilter]);

  useEffect(() => {
    if (selectedWarehouse) {
      loadLocations(selectedWarehouse).then(() => {
        // Auto-select suggested location if it's in this warehouse (for receive tab)
        if (suggestedLocation && String(suggestedLocation.warehouse_id) === selectedWarehouse) {
          setSelectedLocation(String(suggestedLocation.location_id));
        }
      });
    }
  }, [selectedWarehouse, suggestedLocation]);

  // Auto-select first stock location when product is selected on dispatch tab
  useEffect(() => {
    if (selectedTab === 1 && selectedProduct && existingStockLocations.length > 0) {
      // Select the location with the most available stock
      setSelectedLocation(String(existingStockLocations[0].location_id));
    }
  }, [selectedTab, selectedProduct, existingStockLocations]);

  async function loadWarehouses() {
    const data = await fetchApi<{ warehouses: Warehouse[] }>('/inventory/warehouses');
    if (data?.warehouses) {
      setWarehouses(data.warehouses);
      // Set default warehouse
      const defaultWh = data.warehouses.find(w => w.is_default) || data.warehouses[0];
      if (defaultWh) {
        setSelectedWarehouse(String(defaultWh.id));
      }
    }
  }

  async function loadLocations(warehouseId: string) {
    const data = await fetchApi<{ warehouse: Warehouse; locations: Location[] }>(`/inventory/warehouses/${warehouseId}`);
    if (data?.locations) {
      setLocations(data.locations);
      if (data.locations.length > 0 && !selectedLocation) setSelectedLocation(String(data.locations[0].id));
    }
  }

  async function loadMovements() {
    setMovementsLoading(true);
    let url = `/inventory/movements?page=${movementPage}&limit=25`;
    if (movementFilter) {
      url += `&type=${movementFilter}`;
    }
    const data = await fetchApi<{
      movements: Movement[];
      pagination: { page: number; pages: number; total: number };
    }>(url);
    if (data) {
      setMovements(data.movements);
      setMovementTotalPages(data.pagination.pages);
    }
    setMovementsLoading(false);
  }

  async function searchProducts(query: string) {
    if (query.length < 2) {
      setSearchResults([]);
      return;
    }
    const data = await fetchApi<{ products: Product[] }>(`/products?search=${encodeURIComponent(query)}&limit=10`);
    if (data?.products) {
      setSearchResults(data.products);
    }
  }

  async function loadExistingStockLocations(productId: number) {
    const data = await fetchApi<{ stockLocations: StockLocation[]; hasExistingStock: boolean }>(
      `/inventory/product-stock-locations/${productId}`
    );
    if (data?.stockLocations) {
      setExistingStockLocations(data.stockLocations);
      // Set suggested location as the one with highest quantity
      if (data.stockLocations.length > 0) {
        const suggested = data.stockLocations[0]; // Already sorted by quantity DESC
        setSuggestedLocation(suggested);
        // Auto-select the suggested warehouse and location
        setSelectedWarehouse(String(suggested.warehouse_id));
        // Location will be loaded via useEffect when warehouse changes
      } else {
        setSuggestedLocation(null);
      }
    }
  }

  const handleSearchChange = (value: string) => {
    setSearchValue(value);
    searchProducts(value);
  };

  const handleProductSelect = async (product: Product) => {
    setSelectedProduct(product);
    setSearchValue(product.sku);
    setSearchResults([]);
    // Fetch existing stock locations for this product
    await loadExistingStockLocations(product.id);
  };

  const addToItems = () => {
    if (!selectedProduct || !quantity) return;
    const newItem: OperationItem = {
      product_id: selectedProduct.id,
      sku: selectedProduct.sku,
      title: selectedProduct.title,
      quantity: parseInt(quantity, 10),
      reason,
    };
    setItems([...items, newItem]);
    setSelectedProduct(null);
    setSearchValue('');
    setQuantity('1');
    setReason('');
  };

  const removeItem = (index: number) => {
    setItems(items.filter((_, i) => i !== index));
  };

  const resetForm = () => {
    setItems([]);
    setSelectedProduct(null);
    setSearchValue('');
    setQuantity('1');
    setReference('');
    setNotes('');
    setReason('');
    setExistingStockLocations([]);
    setSuggestedLocation(null);
  };

  const handleDispatch = async () => {
    if (items.length === 0 && !selectedProduct) return;

    // Validate location is selected
    const locationId = parseInt(selectedLocation, 10);
    if (!selectedLocation || isNaN(locationId)) {
      alert('Please select a location to dispatch from');
      return;
    }

    setSubmitting(true);
    setSuccess(null);

    const itemsToSend = items.length > 0
      ? items.map(i => ({ ...i, location_id: locationId }))
      : [{ product_id: selectedProduct!.id, location_id: locationId, quantity: parseInt(quantity, 10), reason }];

    const result = await fetchApi<{ success: boolean; processed: number }>('/inventory/dispatch', {
      method: 'POST',
      body: JSON.stringify({
        items: itemsToSend,
        reference,
        notes,
      }),
    });

    if (result?.success) {
      setSuccess(`Successfully dispatched ${result.processed} item(s)`);
      resetForm();
    }
    setSubmitting(false);
  };

  const handleAdjust = async () => {
    if (items.length === 0 && !selectedProduct) return;

    setSubmitting(true);
    setSuccess(null);

    const itemsToSend = items.length > 0 ? items : [{ product_id: selectedProduct!.id, quantity: parseInt(quantity, 10), reason }];

    const result = await fetchApi<{ success: boolean; adjusted: number }>('/inventory/adjust', {
      method: 'POST',
      body: JSON.stringify({
        location_id: parseInt(selectedLocation, 10),
        items: itemsToSend,
        notes,
      }),
    });

    if (result?.success) {
      setSuccess(`Successfully adjusted ${result.adjusted} item(s)`);
      resetForm();
    }
    setSubmitting(false);
  };

  const warehouseOptions = warehouses.map(w => ({ label: w.name, value: String(w.id) }));
  const locationOptions = locations.map(l => ({ label: `${l.code} (${l.zone || 'General'})`, value: String(l.id) }));
  const searchResultOptions = searchResults.map(p => ({
    value: String(p.id),
    label: `${p.sku} - ${p.title}`,
  }));

  const textField = (
    <Autocomplete.TextField
      onChange={handleSearchChange}
      label="Search Product"
      value={searchValue}
      prefix={<Icon source={SearchIcon} />}
      placeholder="Enter SKU, barcode, or product name"
      autoComplete="off"
    />
  );

  const renderProductSearch = () => (
    <Autocomplete
      options={searchResultOptions}
      selected={selectedProduct ? [String(selectedProduct.id)] : []}
      onSelect={(selected) => {
        const product = searchResults.find(p => String(p.id) === selected[0]);
        if (product) handleProductSelect(product);
      }}
      textField={textField}
    />
  );

  const renderItemsTable = () => {
    if (items.length === 0) return null;
    return (
      <DataTable
        columnContentTypes={['text', 'text', 'numeric', 'text', 'text']}
        headings={['SKU', 'Product', 'Qty', 'Reason', '']}
        rows={items.map((item, index) => [
          item.sku,
          item.title.length > 30 ? item.title.substring(0, 30) + '...' : item.title,
          item.quantity.toString(),
          item.reason || '-',
          <Button size="slim" tone="critical" onClick={() => removeItem(index)}>Remove</Button>,
        ])}
      />
    );
  };

  const renderReceivePanel = () => (
    <BlockStack gap="400">
      <Text as="h2" variant="headingMd">Receive a delivery</Text>
      <Text as="p">Count good, damaged and rejected goods. Check the bins, source costs and labels before stock is added.</Text>
      <Button variant="primary" url="/receiving">Open delivery receiving</Button>
      <Text as="p" tone="subdued">Use the customer return workflow for goods coming back from a customer.</Text>
    </BlockStack>
  );

  const renderDispatchPanel = () => {
    // For dispatch, only show locations where the selected product has stock
    const dispatchLocationOptions = selectedProduct && existingStockLocations.length > 0
      ? existingStockLocations.map(sl => ({
          label: `${sl.location_code} - ${sl.warehouse_name} (${sl.available} available)`,
          value: String(sl.location_id),
        }))
      : locationOptions;

    return (
    <BlockStack gap="400">
      <Text as="h2" variant="headingMd">Dispatch / Write-off Stock</Text>
      <Text as="p" tone="subdued">
        Remove stock from inventory for dispatch, damage, or other reasons.
      </Text>

      {renderProductSearch()}

      {selectedProduct && existingStockLocations.length === 0 && (
        <Banner tone="warning">
          This product has no stock in any location.
        </Banner>
      )}

      {selectedProduct && existingStockLocations.length > 0 && (
        <Select
          label="Location (where stock is held)"
          options={dispatchLocationOptions}
          requiredIndicator value={selectedLocation}
          onChange={setSelectedLocation}
          helpText={`Select the location to dispatch from`}
        />
      )}

      {!selectedProduct && (
        <InlineStack gap="400">
          <Select
            label="Warehouse"
            options={warehouseOptions}
            requiredIndicator value={selectedWarehouse}
            onChange={setSelectedWarehouse}
          />
          <Select
            label="Location (Bin/Shelf)"
            options={locationOptions}
            requiredIndicator value={selectedLocation}
            onChange={setSelectedLocation}
            disabled={locationOptions.length === 0}
            helpText={locationOptions.length === 0 ? 'Add locations to this warehouse first' : undefined}
          />
        </InlineStack>
      )}

      <InlineStack gap="400" blockAlign="end">
        <TextField
          label="Quantity"
          type="number"
          requiredIndicator={items.length === 0} value={quantity}
          onChange={setQuantity}
          autoComplete="off"
        />
        <Select
          label="Reason"
          options={[
            { label: 'Damaged', value: 'damaged' },
            { label: 'Expired', value: 'expired' },
            { label: 'Lost', value: 'lost' },
            { label: 'Customer Return (Defective)', value: 'return_defective' },
            { label: 'Disposed', value: 'disposed' },
            { label: 'Other', value: 'other' },
          ]}
          value={reason}
          onChange={setReason}
        />
        <Button icon={PlusIcon} validate onClick={addToItems} disabled={!selectedProduct}>
          Add to List
        </Button>
      </InlineStack>

      {renderItemsTable()}

      <TextField
        label="Reference"
        value={reference}
        onChange={setReference}
        autoComplete="off"
      />
      <TextField
        label="Notes"
        value={notes}
        onChange={setNotes}
        multiline={2}
        autoComplete="off"
      />

      <Button validate
        variant="primary"
        tone="critical"
        onClick={handleDispatch}
        disabled={submitting || (items.length === 0 && !selectedProduct) || !!(selectedProduct && existingStockLocations.length > 0 && !selectedLocation)}
        loading={submitting}
      >
        Dispatch Stock
      </Button>
    </BlockStack>
  );
  };

  const renderTransferPanel = () => (
    <BlockStack gap="400">
      <Text as="h2" variant="headingMd">Move stock</Text>
      <Text as="p">Review scanned goods and selected reservations before moving them between bins.</Text>
      <Button variant="primary" url="/bin-moves">Move stock between bins</Button>
      <Text as="p">Use Warehouse Transfers for shipping and receiving goods between warehouses.</Text>
      <Button url="/transfer-orders">Open warehouse transfers</Button>
    </BlockStack>
  );

  const renderAdjustPanel = () => (
    <BlockStack gap="400">
      <Text as="h2" variant="headingMd">Stock Adjustments</Text>
      <Text as="p" tone="subdued">
        Make corrections to stock levels. Use positive numbers to add, negative to remove.
      </Text>

      <InlineStack gap="400">
        <Select
          label="Warehouse"
          options={warehouseOptions}
          requiredIndicator value={selectedWarehouse}
          onChange={setSelectedWarehouse}
        />
        <Select
          label="Location (Bin/Shelf)"
          options={locationOptions}
          requiredIndicator value={selectedLocation}
          onChange={setSelectedLocation}
          disabled={locationOptions.length === 0}
          helpText={locationOptions.length === 0 ? 'Add locations to this warehouse first' : undefined}
        />
      </InlineStack>

      {renderProductSearch()}

      <InlineStack gap="400" blockAlign="end">
        <TextField
          label="Quantity (+/-)"
          type="number"
          requiredIndicator={items.length === 0} value={quantity}
          onChange={setQuantity}
          helpText="Positive to add, negative to remove"
          autoComplete="off"
        />
        <TextField
          label="Reason"
          value={reason}
          onChange={setReason}
          placeholder="Stock count correction, etc."
          autoComplete="off"
        />
        <Button icon={PlusIcon} validate onClick={addToItems} disabled={!selectedProduct}>
          Add to List
        </Button>
      </InlineStack>

      {renderItemsTable()}

      <TextField
        label="Notes"
        value={notes}
        onChange={setNotes}
        multiline={2}
        autoComplete="off"
      />

      <Button validate
        variant="primary"
        onClick={handleAdjust}
        disabled={submitting || (items.length === 0 && !selectedProduct)}
        loading={submitting}
      >
        Apply Adjustments
      </Button>
    </BlockStack>
  );

  const formatMovementType = (type: string) => {
    const types: Record<string, { label: string; tone: 'success' | 'critical' | 'info' | 'warning' }> = {
      receive: { label: 'Received', tone: 'success' },
      dispatch: { label: 'Dispatched', tone: 'critical' },
      transfer: { label: 'Transfer', tone: 'info' },
      adjust: { label: 'Adjustment', tone: 'warning' },
      count: { label: 'Stock Count', tone: 'info' },
    };
    return types[type] || { label: type, tone: 'info' };
  };

  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    return date.toLocaleString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const renderHistoryPanel = () => (
    <BlockStack gap="400">
      <InlineStack align="space-between">
        <Text as="h2" variant="headingMd">Audit Trail</Text>
        <Select
          label=""
          labelHidden
          options={[
            { label: 'All Types', value: '' },
            { label: 'Received', value: 'receive' },
            { label: 'Dispatched', value: 'dispatch' },
            { label: 'Transfers', value: 'transfer' },
            { label: 'Adjustments', value: 'adjust' },
            { label: 'Stock Counts', value: 'count' },
          ]}
          value={movementFilter}
          onChange={(val) => {
            setMovementFilter(val);
            setMovementPage(1);
          }}
        />
      </InlineStack>

      {movementsLoading ? (
        <Text as="p" tone="subdued">Loading movements...</Text>
      ) : movements.length === 0 ? (
        <Text as="p" tone="subdued">No stock movements recorded yet.</Text>
      ) : (
        <DataTable
          columnContentTypes={['text', 'text', 'text', 'text', 'numeric', 'text', 'text']}
          headings={['Date', 'Type', 'SKU', 'Product', 'Qty', 'Location', 'Reference']}
          rows={movements.map((m) => {
            const typeInfo = formatMovementType(m.type);
            let locationText = '';
            if (m.type === 'transfer') {
              locationText = `${m.from_location_code || '?'} → ${m.to_location_code || '?'}`;
            } else if (m.type === 'receive') {
              locationText = `→ ${m.to_location_code || '?'}`;
            } else {
              locationText = `${m.from_location_code || m.to_location_code || '-'}`;
            }

            return [
              formatDate(m.created_at),
              <Text as="span" tone={typeInfo.tone === 'success' ? 'success' : typeInfo.tone === 'critical' ? 'critical' : undefined}>
                {typeInfo.label}
              </Text>,
              m.sku || '-',
              m.product_title ? (m.product_title.length > 25 ? m.product_title.substring(0, 25) + '...' : m.product_title) : '-',
              m.type === 'dispatch' ? `-${m.quantity}` : `+${m.quantity}`,
              locationText,
              m.reference || m.notes || '-',
            ];
          })}
        />
      )}

      {movementTotalPages > 1 && (
        <InlineStack align="center" gap="200">
          <Button
            disabled={movementPage <= 1}
            onClick={() => setMovementPage(p => p - 1)}
          >
            Previous
          </Button>
          <Text as="span">Page {movementPage} of {movementTotalPages}</Text>
          <Button
            disabled={movementPage >= movementTotalPages}
            onClick={() => setMovementPage(p => p + 1)}
          >
            Next
          </Button>
        </InlineStack>
      )}
    </BlockStack>
  );

  return (
    <Page
      title="Inventory Operations"
      backAction={{ content: 'Inventory', url: '/inventory' }}
    >
      <Layout>
        {(transfers.state.pending || transfers.state.error || transfers.state.blocked) && <Layout.Section>
          <Banner tone="warning">
            <p>{transfers.state.error || 'A saved transfer needs confirmation before another transfer.'}</p>
            {transfers.state.pending && <Button loading={transfers.state.busy} onClick={async () => {
              await transfers.retry();
              const result = transfers.snapshot().success;
              if (result) { setSuccess(`Successfully transferred ${result.transferred} item(s)`); resetForm(); }
            }}>Retry saved transfer</Button>}
          </Banner>
        </Layout.Section>}
        {error && (
          <Layout.Section>
            <Banner tone="critical">{error}</Banner>
          </Layout.Section>
        )}

        {success && (
          <Layout.Section>
            <Banner tone="success" onDismiss={() => setSuccess(null)}>
              {success}
            </Banner>
          </Layout.Section>
        )}

        <Layout.Section>
          <Card>
            <Tabs tabs={tabs} selected={selectedTab} onSelect={setSelectedTab} />
          </Card>
        </Layout.Section>

        <Layout.Section>
          <Card>
            {selectedTab === 0 && renderReceivePanel()}
            {selectedTab === 1 && renderDispatchPanel()}
            {selectedTab === 2 && renderTransferPanel()}
            {selectedTab === 3 && renderAdjustPanel()}
            {selectedTab === 4 && renderHistoryPanel()}
          </Card>
        </Layout.Section>
      </Layout>
    </Page>
  );
}
